import React, { useState, useEffect, useRef, useCallback } from 'react';
import { api, uploadUrl } from '../hooks/useApi';
import DateInput from '../components/DateInput';
import SettingsSecrets from './SettingsSecrets';
import Icon from '../components/Icon';
import './Settings.css';

const ONGLETS = [
  { id: 'general',      label: 'Général' },
  { id: 'visuel',       label: 'Visuel' },
  { id: 'urls',         label: 'URLs' },
  { id: 'fiscal',       label: 'Fiscal' },
  { id: 'sauvegarde',   label: 'Sauvegarde' },
  { id: 'temoignages',  label: 'Témoignages' },
  { id: 'api_claude',   label: 'API Claude' },
  { id: 'entreprise',   label: 'Entreprise' },
  { id: 'associes',     label: 'Associés' },
  { id: 'alertes',      label: 'Alertes' },
  { id: 'integrations', label: 'Intégrations' },
  { id: 'meater',       label: 'MEATER' },
  { id: 'production',   label: 'Production' },
  { id: 'db',           label: 'Base de données' },
  { id: 'secrets',      label: 'Secrets' },
];

// Clés à sauvegarder par onglet
const CLES_PAR_ONGLET = {
  general:  ['nom_entreprise', 'email_contact', 'tel_contact', 'site_url',
             'countdown_actif', 'message_accueil_defaut',
             'hub_titre', 'hub_sous_titre', 'hub_description', 'dates_annoncees'],
  visuel:   ['couleur_primaire'],
  urls:     ['url_backend', 'url_frontend_events', 'url_frontend_corpo', 'url_frontend_app'],
  fiscal:   ['numero_tps', 'numero_tvq', 'neq', 'adresse_escamel', 'acompte_defaut_pct'],
};

// ─── Helpers (récupérés depuis l'ancienne Parametres.jsx) ──────────────────
function fmt$(val) {
  return (val || 0).toLocaleString('fr-CA', { minimumFractionDigits: 2, maximumFractionDigits: 4 }) + '\u00a0$';
}
function fmtDate(str) {
  if (!str) return '—';
  return new Date(str).toLocaleDateString('fr-CA');
}
function fmtDatetime(str) {
  if (!str) return '—';
  const d = new Date(str);
  return d.toLocaleDateString('fr-CA') + ' ' + d.toLocaleTimeString('fr-CA', { hour: '2-digit', minute: '2-digit' });
}
function fmtMo(octets) {
  if (!octets) return '0 o';
  if (octets < 1024) return octets + ' o';
  if (octets < 1024 * 1024) return (octets / 1024).toFixed(1) + ' Ko';
  return (octets / 1024 / 1024).toFixed(1) + ' Mo';
}

function BarreSolde({ pct }) {
  const color = pct > 50 ? 'var(--success)' : pct > 20 ? 'var(--warning)' : 'var(--danger)';
  const largeur = Math.max(0, Math.min(100, pct));
  return (
    <div className="settings-solde-barre-bg">
      <div className="settings-solde-barre" style={{ width: `${largeur}%`, background: color }} />
    </div>
  );
}

function BadgeCache({ tokens }) {
  if (tokens > 0) return <span className="badge badge--info" style={{ fontSize: 11 }}>Cache actif</span>;
  return <span className="badge" style={{ fontSize: 11 }}>Cache inactif</span>;
}

function ModalSolde({ config, onClose, onSave }) {
  const [solde, setSolde]   = useState(String(config?.solde_depart || ''));
  const [date, setDate]     = useState(config?.date_solde_depart || new Date().toISOString().slice(0, 10));
  const [saving, setSaving] = useState(false);

  async function handleSave() {
    if (!solde || isNaN(parseFloat(solde))) return;
    setSaving(true);
    try {
      await api.put('/parametres/api-claude/config', { solde_depart: parseFloat(solde), date_solde_depart: date });
      onSave();
      onClose();
    } catch (e) {
      alert('Erreur: ' + (e.message || 'inconnue'));
    } finally { setSaving(false); }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 420 }}>
        <h3 style={{ fontFamily: 'Cinzel', fontSize: 16, marginBottom: 20 }}>Mettre à jour le solde</h3>
        <div className="field" style={{ marginBottom: 14 }}>
          <label>Nouveau solde ($USD)</label>
          <input type="number" step="0.01" min="0" value={solde} onChange={e => setSolde(e.target.value)} autoFocus />
        </div>
        <div className="field" style={{ marginBottom: 20 }}>
          <label>Date du rechargement</label>
          <DateInput value={date} onChange={v => setDate(v)} />
        </div>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button className="btn btn-ghost" onClick={onClose}>Annuler</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Enregistrement…' : 'Enregistrer'}</button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════
// ONGLET — API Claude
// ═══════════════════════════════════════════════
function OngletApiClaude() {
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [modalSolde, setModalSolde] = useState(false);
  const [configOuvert, setConfigOuvert] = useState(false);
  const [seuil, setSeuil]       = useState('');
  const [clefAdmin, setClefAdmin] = useState('');
  const [showClef, setShowClef] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);
  const [page, setPage]         = useState(1);
  const [usageData, setUsageData] = useState(null);
  const [anthropicData, setAnthropicData] = useState(null);

  const charger = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/parametres/api-claude');
      setData(res);
      setSeuil(String(res.config.seuil_alerte));
    } catch (e) { console.error(e); } finally { setLoading(false); }
  }, []);

  const chargerUsage = useCallback(async (p) => {
    try { const res = await api.get(`/parametres/api-claude/usage?page=${p}&limit=10`); setUsageData(res); } catch {}
  }, []);

  const chargerAnthropic = useCallback(async () => {
    try { const res = await api.get('/parametres/api-claude/usage-anthropic'); setAnthropicData(res); } catch {}
  }, []);

  useEffect(() => { charger(); }, [charger]);
  useEffect(() => { chargerUsage(page); }, [chargerUsage, page]);
  useEffect(() => { chargerAnthropic(); }, [chargerAnthropic]);

  async function sauvegarderConfig() {
    setSavingConfig(true);
    try {
      const payload = { seuil_alerte: parseFloat(seuil) };
      if (clefAdmin !== '') payload.admin_api_key = clefAdmin || null;
      await api.put('/parametres/api-claude/config', payload);
      setClefAdmin('');
      await charger();
    } catch (e) { alert('Erreur: ' + (e.message || 'inconnue')); } finally { setSavingConfig(false); }
  }

  if (loading) return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div className="skeleton" style={{ height: 120, borderRadius: 'var(--radius-lg)' }} />
      <div className="skeleton" style={{ height: 80, borderRadius: 'var(--radius-lg)' }} />
    </div>
  );
  if (!data) return <div className="error-inline"><Icon name="erreur" size={16} /> Erreur de chargement</div>;

  const { config, usage_local, derniers_scans, alerte, statut_api } = data;
  const pctSolde = config.solde_depart > 0 ? (usage_local.solde_estime_restant / config.solde_depart) * 100 : 0;
  const soldeColor = pctSolde > 50 ? 'var(--success)' : pctSolde > 20 ? 'var(--warning)' : 'var(--danger)';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
      {alerte.active && (
        <div className="error-inline">
          <Icon name="alerte" size={16} />
          <div style={{ flex: 1 }}>
            <strong>{alerte.message}</strong>
            <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>Rechargez vos crédits sur console.anthropic.com</div>
          </div>
          <a href="https://console.anthropic.com" target="_blank" rel="noreferrer" className="btn btn-ghost btn--sm">Ouvrir la console</a>
        </div>
      )}

      <div className="card">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--space-4)' }}>
          <h3 className="settings-card-titre" style={{ margin: 0 }}>API Claude — Statut</h3>
          <div className="settings-status">
            <span className={`settings-status-dot settings-status-dot--${statut_api === 'operationnel' ? 'on' : 'off'}`} />
            <span style={{ color: statut_api === 'operationnel' ? 'var(--success)' : 'var(--danger)', fontSize: 12 }}>
              {statut_api === 'operationnel' ? 'Opérationnel' : 'Alerte solde'}
            </span>
          </div>
        </div>
        <div style={{ textAlign: 'center', padding: 'var(--space-3) 0' }}>
          <div className="muted" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 4 }}>Solde estimé restant</div>
          <div style={{ fontSize: 38, fontWeight: 600, color: soldeColor, fontFamily: 'Cinzel', fontFeatureSettings: '"tnum" 1, "lnum" 1' }}>{fmt$(usage_local.solde_estime_restant)}</div>
          <BarreSolde pct={pctSolde} />
          <div className="muted" style={{ fontSize: 12 }}>{pctSolde.toFixed(0)} %</div>
          {pctSolde < 20 && <span className="badge badge--danger" style={{ marginTop: 6 }}>Solde bas</span>}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-2)', margin: 'var(--space-3) 0', fontSize: 13 }}>
          <div className="muted">Solde de départ</div>
          <div>{fmt$(config.solde_depart)} <span className="muted">({fmtDate(config.date_solde_depart)})</span></div>
          <div className="muted">Dépensé depuis</div>
          <div>{fmt$(usage_local.cout_depuis_solde)}</div>
        </div>
        <button className="btn btn-ghost btn--sm" style={{ marginTop: 'var(--space-2)' }} onClick={() => setModalSolde(true)}>
          <Icon name="modifier" size={14} /> Mettre à jour le solde
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 'var(--space-3)' }}>
        {[
          { label: 'Scans total',      val: usage_local.nb_scans_total,      sub: `dont ${usage_local.nb_scans_mois} ce mois`, size: 28 },
          { label: 'Coût moyen / scan', val: fmt$(usage_local.cout_moyen_par_scan), size: 22 },
          { label: 'Ce mois',          val: fmt$(usage_local.cout_mois_courant),    sub: `${usage_local.nb_scans_mois} scans`, size: 22 },
        ].map(({ label, val, sub, size }) => (
          <div key={label} className="card" style={{ textAlign: 'center' }}>
            <div className="muted" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 4 }}>{label}</div>
            <div style={{ fontSize: size, fontWeight: 600, fontFamily: 'Cinzel', fontFeatureSettings: '"tnum" 1' }}>{val}</div>
            {sub && <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>{sub}</div>}
          </div>
        ))}
        <div className="card" style={{ textAlign: 'center' }}>
          <div className="muted" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 4 }}>Cache économisé</div>
          <div style={{ fontSize: 22, fontWeight: 600, fontFamily: 'Cinzel', color: 'var(--success)' }}>{fmt$(usage_local.cache_economie_totale)}</div>
        </div>
        <div className="card" style={{ textAlign: 'center' }}>
          <div className="muted" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 4 }}>Coût officiel Anthropic</div>
          {anthropicData?.disponible && anthropicData.data ? (
            <div style={{ fontSize: 18, fontWeight: 600, fontFamily: 'Cinzel' }}>{fmt$(anthropicData.data?.total_cost || 0)}</div>
          ) : config.admin_api_key_configuree ? (
            <div className="muted" style={{ fontSize: 12 }}>Chargement…</div>
          ) : (
            <button className="btn btn-ghost btn--sm" style={{ marginTop: 4 }} onClick={() => setConfigOuvert(true)}>
              Configurer clé admin
            </button>
          )}
        </div>
      </div>

      <div className="card">
        <h3 className="settings-card-titre">Historique des scans</h3>
        <div style={{ overflowX: 'auto' }}>
          <table className="esc-table">
            <thead>
              <tr>
                <th>Date</th><th>Associé</th><th>Mode</th><th>Fournisseur</th>
                <th>Transactions</th><th>Coût</th><th>Cache</th><th>Statut</th>
              </tr>
            </thead>
            <tbody>
              {(usageData?.rows || derniers_scans).map(scan => (
                <tr key={scan.id}>
                  <td style={{ fontSize: 12 }}>{fmtDatetime(scan.cree_le)}</td>
                  <td><span className="badge">{scan.associe || '—'}</span></td>
                  <td style={{ fontSize: 12 }}>{scan.mode === 'escamel' ? 'Escamel' : scan.mode === 'perso' ? 'Perso' : scan.mode === 'inventaire_seulement' ? 'Inv. seul.' : scan.mode || '—'}</td>
                  <td style={{ fontSize: 12 }}>{scan.fournisseur_detecte || '—'}</td>
                  <td style={{ textAlign: 'center' }}>{scan.nb_transactions_detectees ?? '—'}</td>
                  <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{fmt$(scan.cout_total)}</td>
                  <td><BadgeCache tokens={scan.cache_read_tokens} /></td>
                  <td>
                    {scan.succes
                      ? <span className="badge badge--success">OK</span>
                      : <span className="badge badge--danger">Echec</span>}
                  </td>
                </tr>
              ))}
              {(usageData?.rows || derniers_scans).length === 0 && (
                <tr><td colSpan={8}>
                  <div className="empty-state" style={{ padding: 'var(--space-5) var(--space-4)' }}>
                    <Icon name="analytics" size={28} />
                    <span>Aucun scan enregistré</span>
                  </div>
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
        {usageData && usageData.total > 10 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', marginTop: 'var(--space-3)', justifyContent: 'flex-end' }}>
            <button className="btn btn-ghost btn--sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Précédent</button>
            <span className="muted" style={{ fontSize: 13 }}>Page {page} / {Math.ceil(usageData.total / 10)}</span>
            <button className="btn btn-ghost btn--sm" disabled={page >= Math.ceil(usageData.total / 10)} onClick={() => setPage(p => p + 1)}>Suivant</button>
          </div>
        )}
      </div>

      <div className="card">
        <button
          style={{ background: 'none', border: 'none', color: 'var(--text)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 'var(--space-2)', padding: 0, width: '100%', textAlign: 'left' }}
          onClick={() => setConfigOuvert(o => !o)}
        >
          <Icon name="parametres" size={16} />
          <span style={{ fontFamily: 'Cinzel', fontSize: 13, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.08em', color: 'var(--text-soft)' }}>Configuration avancée</span>
          <Icon name="chevron" size={14} color="var(--muted)" style={{ marginLeft: 'auto', transform: configOuvert ? 'rotate(90deg)' : 'none', transition: 'transform var(--t-fast)' }} />
        </button>
        {configOuvert && (
          <div style={{ marginTop: 'var(--space-5)', display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
            <div className="field">
              <label>Seuil d'alerte solde bas ($USD)</label>
              <input type="number" step="0.50" min="0" value={seuil} onChange={e => setSeuil(e.target.value)} style={{ maxWidth: 180 }} />
            </div>
            <div className="field">
              <label>Clé Admin API Anthropic <span className="muted">(optionnel)</span></label>
              <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
                <input type={showClef ? 'text' : 'password'} placeholder={config.admin_api_key_configuree ? '••••••••••••••••••••' : 'sk-ant-admin...'} value={clefAdmin} onChange={e => setClefAdmin(e.target.value)} style={{ flex: 1 }} />
                <button className="btn btn-ghost btn--sm" onClick={() => setShowClef(v => !v)}>
                  <Icon name="voir" size={14} />
                </button>
              </div>
              <span className="field-hint">
                Permet d'afficher le coût officiel depuis la console Anthropic.
                {config.admin_api_key_configuree && ' Clé déjà configurée — laisser vide pour conserver.'}
              </span>
            </div>
            <button className="btn btn-primary btn--sm" style={{ alignSelf: 'flex-start' }} onClick={sauvegarderConfig} disabled={savingConfig}>
              {savingConfig ? 'Enregistrement…' : 'Enregistrer'}
            </button>
          </div>
        )}
      </div>

      {modalSolde && <ModalSolde config={config} onClose={() => setModalSolde(false)} onSave={charger} />}
    </div>
  );
}

// ═══════════════════════════════════════════════
// ONGLET — Entreprise (identité, logo PDF, fiscal)
// ═══════════════════════════════════════════════
function OngletEntreprise() {
  const [form, setForm]             = useState(null);
  const [saving, setSaving]         = useState(false);
  const [msg, setMsg]               = useState(null);
  const [uploadingLogo, setUploadingLogo]       = useState(false);
  const [uploadingFavicon, setUploadingFavicon] = useState(false);
  const [faviconTs, setFaviconTs]               = useState(null); // null = pas de favicon
  const fileRef                     = useRef();
  const faviconRef                  = useRef();

  useEffect(() => {
    api.get('/parametres/general').then(setForm).catch(() => {});
    fetch('/api/parametres/general/favicon')
      .then(r => { if (r.ok) setFaviconTs(Date.now()); })
      .catch(() => {});
  }, []);

  function champ(key, val) { setForm(f => ({ ...f, [key]: val })); }

  async function enregistrer() {
    setSaving(true); setMsg(null);
    try {
      await api.put('/parametres/general', form);
      setMsg({ type: 'success', text: 'Paramètres enregistrés.' });
    } catch (e) {
      setMsg({ type: 'danger', text: e.message || 'Erreur' });
    } finally { setSaving(false); }
  }

  async function uploaderFavicon(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingFavicon(true);
    try {
      const fd = new FormData();
      fd.append('favicon', file);
      const res = await fetch('/api/parametres/general/favicon', {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('escamel_token')}` },
        body: fd,
      });
      const json = await res.json();
      if (json.success) {
        const ts = Date.now();
        setFaviconTs(ts);
        // Mettre à jour le favicon de l'onglet
        const link = document.getElementById('dynamic-favicon');
        if (link) link.href = `/api/parametres/general/favicon?t=${ts}`;
      } else {
        alert('Erreur: ' + (json.error || 'inconnue'));
      }
    } catch (err) {
      alert('Erreur upload favicon: ' + err.message);
    } finally {
      setUploadingFavicon(false);
      if (faviconRef.current) faviconRef.current.value = '';
    }
  }

  async function uploaderLogoEntreprise(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingLogo(true);
    try {
      const fd = new FormData();
      fd.append('logo', file);
      const res = await fetch('/api/parametres/general/logo', {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('escamel_token')}` },
        body: fd,
      });
      const json = await res.json();
      if (json.success) setForm(f => ({ ...f, logo_fichier: json.logo_fichier }));
    } catch (e) {
      alert('Erreur upload logo: ' + e.message);
    } finally { setUploadingLogo(false); }
  }

  if (!form) return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
      <div className="skeleton skel-settings-block" />
      <div className="skeleton skel-settings-block" />
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)', maxWidth: 640 }}>
      <div className="card">
        <h3 className="settings-card-titre"><Icon name="entreprise" size={15} style={{ marginRight: 6 }} /> Identité de l'entreprise</h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
          {[
            { label: 'Nom',         key: 'nom_entreprise' },
            { label: 'NEQ',         key: 'neq' },
            { label: 'Adresse',     key: 'adresse' },
            { label: 'Ville',       key: 'ville' },
            { label: 'Province',    key: 'province' },
            { label: 'Code postal', key: 'code_postal' },
            { label: 'Téléphone',   key: 'telephone' },
            { label: 'Courriel',    key: 'courriel' },
            { label: 'Site web',    key: 'site_web' },
          ].map(({ label, key }) => (
            <div className="field" key={key}>
              <label>{label}</label>
              <input value={form[key] || ''} onChange={e => champ(key, e.target.value)} />
            </div>
          ))}
        </div>
      </div>

      <div className="card">
        <h3 className="settings-card-titre"><Icon name="photo" size={15} style={{ marginRight: 6 }} /> Logo (PDF)</h3>
        <div className="settings-upload-zone">
          <div className="settings-upload-preview">
            {form.logo_fichier
              ? <img src={uploadUrl(`/uploads/logo/${form.logo_fichier}`)} alt="Logo" />
              : <Icon name="entreprise" size={28} color="var(--muted)" />}
          </div>
          <div className="settings-upload-info">
            <input ref={fileRef} type="file" accept=".png,.jpg,.jpeg,.svg" style={{ display: 'none' }} onChange={uploaderLogoEntreprise} />
            <button className="btn btn-ghost btn--sm" onClick={() => fileRef.current?.click()} disabled={uploadingLogo}>
              <Icon name="televerser" size={14} /> {uploadingLogo ? 'Envoi…' : 'Changer le logo'}
            </button>
            <span className="muted" style={{ fontSize: 11 }}>PNG, JPG, SVG — max 2 Mo</span>
            <span className="muted" style={{ fontSize: 11 }}>Utilisé dans les exports PDF et l'entête des contrats</span>
          </div>
        </div>
      </div>

      <div className="card">
        <h3 className="settings-card-titre"><Icon name="boutique" size={15} style={{ marginRight: 6 }} /> Favicon</h3>
        <div className="settings-upload-zone">
          <div className="settings-upload-preview" style={{ width: 48, height: 48 }}>
            {faviconTs
              ? <FaviconPreview ts={faviconTs} />
              : <Icon name="boutique" size={22} color="var(--muted)" />}
          </div>
          <div className="settings-upload-info">
            <input ref={faviconRef} type="file" accept=".ico,.png,.svg" style={{ display: 'none' }} onChange={uploaderFavicon} />
            <button className="btn btn-ghost btn--sm" onClick={() => faviconRef.current?.click()} disabled={uploadingFavicon}>
              <Icon name="televerser" size={14} /> {uploadingFavicon ? 'Envoi…' : faviconTs ? 'Changer le favicon' : 'Uploader un favicon'}
            </button>
            <span className="muted" style={{ fontSize: 11 }}>ICO, PNG, SVG — max 512 Ko</span>
            <span className="muted" style={{ fontSize: 11 }}>Icône affichée dans l'onglet du navigateur</span>
          </div>
        </div>
      </div>

      <div className="card">
        <h3 className="settings-card-titre"><Icon name="date" size={15} style={{ marginRight: 6 }} /> Exercice fiscal</h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
          <div className="field">
            <label>Année fiscale active</label>
            <select value={form.annee_fiscale_active || 2026} onChange={e => champ('annee_fiscale_active', parseInt(e.target.value))} style={{ maxWidth: 120 }}>
              {[2025, 2026, 2027, 2028].map(a => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>
          <div className="field">
            <label>Format montants</label>
            <select value={form.format_montant || 'fr-CA'} onChange={e => champ('format_montant', e.target.value)} style={{ maxWidth: 220 }}>
              <option value="fr-CA">1 234,56 $ (fr-CA)</option>
              <option value="en-CA">$1,234.56 (en-CA)</option>
            </select>
          </div>
        </div>
      </div>

      {msg && (
        <div className={msg.type === 'success' ? 'settings-alerte settings-succes' : 'settings-alerte settings-erreur'}>
          <Icon name={msg.type === 'success' ? 'succes' : 'erreur'} size={15} />
          {msg.text}
        </div>
      )}
      <button className="btn btn-primary" onClick={enregistrer} disabled={saving} style={{ alignSelf: 'flex-start' }}>
        <Icon name="sauvegarder" size={15} /> {saving ? 'Enregistrement…' : 'Enregistrer'}
      </button>
    </div>
  );
}

// ═══════════════════════════════════════════════
// ONGLET — Associés
// ═══════════════════════════════════════════════
function CarteAssocie({ associe: init, onSave }) {
  const [form, setForm]         = useState({ ...init });
  const [saving, setSaving]     = useState(false);
  const [msg, setMsg]           = useState(null);
  const [nasVisible, setNasVisible]   = useState(false);
  const [nasReel, setNasReel]         = useState(null);
  const [loadingNas, setLoadingNas]   = useState(false);

  function champ(key, val) { setForm(f => ({ ...f, [key]: val })); }

  async function revelerNas() {
    if (nasVisible) { setNasVisible(false); return; }
    setLoadingNas(true);
    try {
      const res = await api.get(`/parametres/associes/${init.code}/nas`);
      setNasReel(res.nas || '');
      setNasVisible(true);
    } catch {} finally { setLoadingNas(false); }
  }

  async function enregistrer() {
    setSaving(true); setMsg(null);
    try {
      await api.put(`/parametres/associes/${init.code}`, form);
      setMsg({ type: 'success', text: 'Enregistré.' });
      onSave();
    } catch (e) {
      setMsg({ type: 'danger', text: e.message || 'Erreur' });
    } finally { setSaving(false); }
  }

  return (
    <div className="card" style={{ flex: 1, minWidth: 260 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', marginBottom: 'var(--space-4)' }}>
        <div className="settings-avatar">{init.code}</div>
        <div>
          <div style={{ fontWeight: 600 }}>{form.nom_complet}</div>
          <div className="muted" style={{ fontSize: 12 }}>{form.titre} — {form.part_societe_pct} %</div>
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
        <div className="field">
          <label>Nom complet</label>
          <input value={form.nom_complet || ''} onChange={e => champ('nom_complet', e.target.value)} />
        </div>
        <div className="field">
          <label>Titre</label>
          <input value={form.titre || ''} onChange={e => champ('titre', e.target.value)} />
        </div>
        <div className="field">
          <label>NAS</label>
          <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
            <input
              type="text"
              placeholder="•••-•••-•••"
              value={nasVisible ? (nasReel !== null ? nasReel : '') : (form.nas_configure ? '•••-•••-•••' : '')}
              onChange={e => { if (nasVisible) setNasReel(e.target.value); else champ('nas', e.target.value); }}
              onBlur={() => { if (nasVisible && nasReel !== null) champ('nas', nasReel); }}
              style={{ flex: 1 }}
            />
            <button className="btn btn-ghost btn--sm" style={{ flexShrink: 0 }} onClick={revelerNas} disabled={loadingNas} title={nasVisible ? 'Masquer' : 'Révéler'}>
              {loadingNas ? '…' : <Icon name="voir" size={14} />}
            </button>
          </div>
        </div>
        {[
          { label: 'Adresse',     key: 'adresse' },
          { label: 'Ville',       key: 'ville' },
          { label: 'Province',    key: 'province' },
          { label: 'Code postal', key: 'code_postal' },
          { label: 'Téléphone',   key: 'telephone' },
          { label: 'Courriel',    key: 'courriel' },
        ].map(({ label, key }) => (
          <div className="field" key={key}>
            <label>{label}</label>
            <input value={form[key] || ''} onChange={e => champ(key, e.target.value)} />
          </div>
        ))}
        <div className="muted" style={{ fontSize: 12, padding: '6px 10px', background: 'var(--surface)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)' }}>
          Part société : {form.part_societe_pct} % (non modifiable)
        </div>
      </div>
      {msg && (
        <div className={msg.type === 'success' ? 'settings-alerte settings-succes' : 'settings-alerte settings-erreur'} style={{ marginTop: 'var(--space-3)' }}>
          {msg.text}
        </div>
      )}
      <button className="btn btn-primary btn--block" style={{ marginTop: 'var(--space-4)' }} onClick={enregistrer} disabled={saving}>
        <Icon name="sauvegarder" size={14} /> {saving ? 'Enregistrement…' : `Enregistrer ${init.code}`}
      </button>
    </div>
  );
}

function OngletAssocies() {
  const [associes, setAssocies] = useState([]);
  const charger = useCallback(() => {
    api.get('/parametres/associes').then(setAssocies).catch(() => {});
  }, []);
  useEffect(() => { charger(); }, [charger]);
  if (!associes.length) return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
      <div className="skeleton skel-settings-block" />
    </div>
  );
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
      <div style={{ display: 'flex', gap: 'var(--space-5)', flexWrap: 'wrap' }}>
        {associes.map(a => <CarteAssocie key={a.code} associe={a} onSave={charger} />)}
      </div>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-2)', padding: '10px 14px', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', maxWidth: 640 }}>
        <Icon name="info" size={14} color="var(--muted)" style={{ marginTop: 1 }} />
        <span className="muted" style={{ fontSize: 12 }}>Le NAS est utilisé uniquement pour les fiches fiscales T2125/TP-80. Il est stocké localement et n'est jamais transmis à des tiers.</span>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════
// ONGLET — Alertes
// ═══════════════════════════════════════════════
function OngletAlertes() {
  const [form, setForm]   = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg]     = useState(null);

  useEffect(() => {
    api.get('/parametres/alertes').then(setForm).catch(() => {});
  }, []);

  function champ(key, val) { setForm(f => ({ ...f, [key]: val })); }

  async function enregistrer() {
    setSaving(true); setMsg(null);
    try {
      await api.put('/parametres/alertes', form);
      setMsg({ type: 'success', text: 'Alertes enregistrées.' });
    } catch (e) {
      setMsg({ type: 'danger', text: e.message || 'Erreur' });
    } finally { setSaving(false); }
  }

  if (!form) return (
    <div className="skeleton skel-settings-block" />
  );

  return (
    <div style={{ maxWidth: 580 }}>
      <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
        <h3 className="settings-card-titre"><Icon name="notification" size={15} style={{ marginRight: 6 }} /> Configuration des alertes</h3>

        <div className="settings-alerte-section">
          <div className="settings-alerte-label">Inventaire</div>
          <label className="settings-checkbox-row">
            <input type="checkbox" checked={!!form.alerte_inventaire_actif} onChange={e => champ('alerte_inventaire_actif', e.target.checked ? 1 : 0)} />
            <span>Alerter quand un stock passe sous son seuil</span>
          </label>
          <div className="muted" style={{ fontSize: 12, paddingLeft: 'var(--space-7)' }}>Seuils configurés par matière dans Inventaire — Matières premières</div>
        </div>

        <div className="settings-alerte-section">
          <div className="settings-alerte-label">Taxes TPS/TVQ</div>
          <label className="settings-checkbox-row">
            <input type="checkbox" checked={!!form.alerte_tps_trimestre} onChange={e => champ('alerte_tps_trimestre', e.target.checked ? 1 : 0)} />
            <span>Rappel avant échéance de déclaration</span>
          </label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', marginTop: 'var(--space-2)', paddingLeft: 'var(--space-7)' }}>
            <span className="muted" style={{ fontSize: 13 }}>Délai :</span>
            <input type="number" min={1} max={90} style={{ width: 70, background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', color: 'var(--text)', padding: '4px 8px' }} value={form.rappel_tps_jours_avant || 14} onChange={e => champ('rappel_tps_jours_avant', parseInt(e.target.value))} />
            <span className="muted" style={{ fontSize: 13 }}>jours avant échéance</span>
          </div>
        </div>

        <div className="settings-alerte-section">
          <div className="settings-alerte-label">Cloture d'annee</div>
          <label className="settings-checkbox-row">
            <input type="checkbox" checked={!!form.alerte_cloture_annee} onChange={e => champ('alerte_cloture_annee', e.target.checked ? 1 : 0)} />
            <span>Rappel de clôture annuelle</span>
          </label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', marginTop: 'var(--space-2)', paddingLeft: 'var(--space-7)' }}>
            <span className="muted" style={{ fontSize: 13 }}>Délai :</span>
            <input type="number" min={1} max={180} style={{ width: 70, background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', color: 'var(--text)', padding: '4px 8px' }} value={form.rappel_cloture_jours_avant || 30} onChange={e => champ('rappel_cloture_jours_avant', parseInt(e.target.value))} />
            <span className="muted" style={{ fontSize: 13 }}>jours avant le 31 déc.</span>
          </div>
        </div>

        <div className="settings-alerte-section">
          <div className="settings-alerte-label">Notifications</div>
          <div className="field">
            <label>Courriel pour alertes <span className="muted">(optionnel)</span></label>
            <input type="email" placeholder="vous@example.com" value={form.courriel_alertes || ''} onChange={e => champ('courriel_alertes', e.target.value)} />
            <span className="field-hint">Laisser vide = alertes dans l'app seulement</span>
          </div>
        </div>

        {msg && (
          <div className={msg.type === 'success' ? 'settings-alerte settings-succes' : 'settings-alerte settings-erreur'} style={{ margin: 'var(--space-3) 0 0' }}>
            <Icon name={msg.type === 'success' ? 'succes' : 'erreur'} size={14} /> {msg.text}
          </div>
        )}
        <div style={{ paddingTop: 'var(--space-4)' }}>
          <button className="btn btn-primary btn--sm" onClick={enregistrer} disabled={saving}>
            <Icon name="sauvegarder" size={14} /> {saving ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════
// ONGLET — Intégrations (configs NON-secrètes uniquement)
// Les clés API / mots de passe sont gérés dans l'onglet Secrets 🔐
// ═══════════════════════════════════════════════
function OngletIntegrations({ onAllerSecrets }) {
  const [form, setForm]       = useState(null);
  const [saving, setSaving]   = useState(false);
  const [msg, setMsg]         = useState(null);
  const [testWc, setTestWc]   = useState(null);
  const [testingWc, setTestingWc] = useState(false);

  useEffect(() => {
    api.get('/parametres/integrations').then(d => { setForm(d); }).catch(() => {});
  }, []);

  function champ(key, val) { setForm(f => ({ ...f, [key]: val })); }

  async function enregistrer() {
    setSaving(true); setMsg(null);
    try {
      // On n'envoie QUE les configs non-secrètes. Le backend préserve les secrets DB existants.
      const payload = {
        wc_url: form.wc_url,
        wc_actif: form.wc_actif,
        smtp_host: form.smtp_host,
        smtp_port: form.smtp_port,
        smtp_user: form.smtp_user,
        smtp_from: form.smtp_from,
        smtp_actif: form.smtp_actif,
        stripe_actif: form.stripe_actif,
        brevo_sender_email: form.brevo_sender_email,
        brevo_sender_name: form.brevo_sender_name,
        brevo_actif: form.brevo_actif,
      };
      await api.put('/parametres/integrations', payload);
      const updated = await api.get('/parametres/integrations');
      setForm(updated);
      setMsg({ type: 'success', text: 'Intégrations enregistrées.' });
    } catch (e) {
      setMsg({ type: 'danger', text: e.message || 'Erreur' });
    } finally { setSaving(false); }
  }

  async function testerWc() {
    setTestingWc(true); setTestWc(null);
    try {
      const res = await api.post('/parametres/integrations/test-wc', {});
      setTestWc(res);
    } catch { setTestWc({ succes: false, message: 'Erreur réseau' }); }
    finally { setTestingWc(false); }
  }

  if (!form) return <div className="skeleton skel-settings-block" />;

  function StatutIntegration({ actif }) {
    return (
      <div className="settings-status">
        <span className={`settings-status-dot settings-status-dot--${actif ? 'on' : 'off'}`} />
        <span style={{ color: actif ? 'var(--success)' : 'var(--muted)', fontSize: 12 }}>{actif ? 'Actif' : 'Inactif'}</span>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)', maxWidth: 640 }}>

      {/* Bannière Secrets */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', padding: '11px 16px', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', fontSize: 13 }}>
        <Icon name="verrou" size={16} color="var(--accent)" />
        <div style={{ flex: 1 }}>
          <strong>Clés API et mots de passe</strong> sont gérés dans l'onglet <strong>Secrets</strong> (rotation + test + audit).
        </div>
        <button className="btn btn-ghost btn--sm" onClick={onAllerSecrets}>Ouvrir Secrets</button>
      </div>

      {/* WooCommerce */}
      <div className="card">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--space-4)' }}>
          <h3 className="settings-card-titre" style={{ margin: 0 }}>WooCommerce</h3>
          <StatutIntegration actif={form.wc_actif} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
          <div className="field">
            <label>URL du site</label>
            <input value={form.wc_url || ''} onChange={e => champ('wc_url', e.target.value)} placeholder="https://escamel.ca" />
          </div>
          <label className="settings-checkbox-row">
            <input type="checkbox" checked={!!form.wc_actif} onChange={e => champ('wc_actif', e.target.checked ? 1 : 0)} />
            <span style={{ fontSize: 13 }}>Intégration WooCommerce active</span>
          </label>
          <span className="muted" style={{ fontSize: 11 }}>Clé et secret : onglet <strong>Secrets</strong> (WC_KEY + WC_SECRET)</span>
        </div>
        <div style={{ display: 'flex', gap: 'var(--space-3)', marginTop: 'var(--space-4)', alignItems: 'center', flexWrap: 'wrap' }}>
          <button className="btn btn-ghost btn--sm" onClick={testerWc} disabled={testingWc}>
            <Icon name="rafraichir" size={13} /> {testingWc ? 'Test…' : 'Tester la connexion'}
          </button>
          {testWc && (
            <span style={{ fontSize: 13, color: testWc.succes ? 'var(--success)' : 'var(--danger)', display: 'flex', alignItems: 'center', gap: 4 }}>
              <Icon name={testWc.succes ? 'succes' : 'erreur'} size={14} /> {testWc.message}
            </span>
          )}
        </div>
      </div>

      {/* SMTP */}
      <div className="card">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--space-4)' }}>
          <h3 className="settings-card-titre" style={{ margin: 0 }}>SMTP — Envoi de courriels</h3>
          <StatutIntegration actif={form.smtp_actif} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
          {[
            { label: 'Serveur SMTP', key: 'smtp_host', placeholder: 'smtp.gmail.com' },
            { label: 'Utilisateur',  key: 'smtp_user', placeholder: 'user@example.com' },
            { label: 'Expéditeur',   key: 'smtp_from', placeholder: 'Escamel <noreply@escamel.ca>' },
          ].map(({ label, key, placeholder }) => (
            <div className="field" key={key}>
              <label>{label}</label>
              <input value={form[key] || ''} onChange={e => champ(key, e.target.value)} placeholder={placeholder} />
            </div>
          ))}
          <div className="field">
            <label>Port</label>
            <input type="number" style={{ maxWidth: 100 }} value={form.smtp_port || 587} onChange={e => champ('smtp_port', parseInt(e.target.value))} />
          </div>
          <label className="settings-checkbox-row">
            <input type="checkbox" checked={!!form.smtp_actif} onChange={e => champ('smtp_actif', e.target.checked ? 1 : 0)} />
            <span style={{ fontSize: 13 }}>SMTP actif</span>
          </label>
          <span className="muted" style={{ fontSize: 11 }}>Mot de passe : onglet <strong>Secrets</strong> (SMTP_PASS)</span>
        </div>
      </div>

      {/* Stripe */}
      <div className="card">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--space-4)' }}>
          <h3 className="settings-card-titre" style={{ margin: 0 }}>Stripe</h3>
          <StatutIntegration actif={form.stripe_actif} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
          <label className="settings-checkbox-row">
            <input type="checkbox" checked={!!form.stripe_actif} onChange={e => champ('stripe_actif', e.target.checked ? 1 : 0)} />
            <span style={{ fontSize: 13 }}>Intégration Stripe active</span>
          </label>
          <span className="muted" style={{ fontSize: 11 }}>Clé secrète et webhook : onglet <strong>Secrets</strong> (STRIPE_SECRET_KEY + STRIPE_WEBHOOK_SECRET)</span>
        </div>
      </div>

      {/* Brevo */}
      <div className="card">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--space-4)' }}>
          <h3 className="settings-card-titre" style={{ margin: 0 }}>Brevo — Courriels transactionnels</h3>
          <StatutIntegration actif={form.brevo_actif} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
          {[
            { label: 'Courriel expéditeur', key: 'brevo_sender_email', placeholder: 'noreply@escamel.ca' },
            { label: "Nom de l'expéditeur", key: 'brevo_sender_name',  placeholder: 'Escamel Artisans du Fumoir' },
          ].map(({ label, key, placeholder }) => (
            <div className="field" key={key}>
              <label>{label}</label>
              <input value={form[key] || ''} onChange={e => champ(key, e.target.value)} placeholder={placeholder} />
            </div>
          ))}
          <label className="settings-checkbox-row">
            <input type="checkbox" checked={!!form.brevo_actif} onChange={e => champ('brevo_actif', e.target.checked ? 1 : 0)} />
            <span style={{ fontSize: 13 }}>Brevo actif</span>
          </label>
          <span className="muted" style={{ fontSize: 11 }}>Clé API : onglet <strong>Secrets</strong> (BREVO_API_KEY)</span>
        </div>
      </div>

      {/* Anthropic */}
      <div className="card">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h3 className="settings-card-titre" style={{ margin: 0 }}>Anthropic (Claude)</h3>
          <span className="muted" style={{ fontSize: 12 }}>Aucune config non-secrète</span>
        </div>
        <div className="muted" style={{ fontSize: 11, marginTop: 'var(--space-2)' }}>
          Clé API : onglet <strong>Secrets</strong> (ANTHROPIC_API_KEY)
        </div>
      </div>

      {msg && (
        <div className={msg.type === 'success' ? 'settings-alerte settings-succes' : 'settings-alerte settings-erreur'}>
          <Icon name={msg.type === 'success' ? 'succes' : 'erreur'} size={14} /> {msg.text}
        </div>
      )}
      <button className="btn btn-primary btn--sm" onClick={enregistrer} disabled={saving} style={{ alignSelf: 'flex-start' }}>
        <Icon name="sauvegarder" size={14} /> {saving ? 'Enregistrement…' : 'Enregistrer'}
      </button>
    </div>
  );
}

// ═══════════════════════════════════════════════
// ONGLET — MEATER (sondes de cuisson)
// ═══════════════════════════════════════════════
function OngletMeater() {
  const [config, setConfig]       = useState(null);
  const [email, setEmail]         = useState('');
  const [savingEmail, setSavingEmail] = useState(false);
  const [devices, setDevices]     = useState([]);
  const [chargementDevices, setChargementDevices] = useState(false);
  const [erreurDevices, setErreurDevices] = useState(null);
  const [nomsEdition, setNomsEdition] = useState({}); // device_id → nouveau nom
  const [savingNom, setSavingNom] = useState({});     // device_id → bool
  const [msg, setMsg]             = useState(null);

  // Chargement initial de la config
  useEffect(() => {
    api.get('/meater/config')
      .then(d => {
        setConfig(d || {});
        setEmail(d?.email || '');
      })
      .catch(() => setConfig({}));
  }, []);

  async function enregistrerEmail() {
    if (!email.trim()) {
      setMsg({ type: 'danger', text: 'Veuillez saisir un courriel.' });
      return;
    }
    setSavingEmail(true); setMsg(null);
    try {
      await api.post('/meater/config', { email: email.trim() });
      const updated = await api.get('/meater/config');
      setConfig(updated || {});
      setMsg({ type: 'success', text: 'Courriel MEATER enregistré. Token réinitialisé.' });
    } catch (e) {
      setMsg({ type: 'danger', text: e.response?.data?.error || e.message || 'Erreur' });
    } finally { setSavingEmail(false); }
  }

  async function rafraichirDevices() {
    setChargementDevices(true); setErreurDevices(null);
    try {
      const liste = await api.get('/meater/devices');
      setDevices(Array.isArray(liste) ? liste : []);
      // Rafraîchit aussi la config pour voir le token_obtenu_le mis à jour
      const updated = await api.get('/meater/config');
      setConfig(updated || {});
    } catch (e) {
      setErreurDevices(e.response?.data?.error || e.message || 'Erreur');
    } finally { setChargementDevices(false); }
  }

  async function renommerDevice(device_id) {
    const nouveauNom = (nomsEdition[device_id] || '').trim();
    if (!nouveauNom) return;
    setSavingNom(s => ({ ...s, [device_id]: true }));
    try {
      await api.put(`/meater/devices/${encodeURIComponent(device_id)}/nom`, { nom: nouveauNom });
      // Mise à jour locale
      setDevices(ds => ds.map(d =>
        d.id === device_id ? { ...d, nom: nouveauNom } : d
      ));
      setNomsEdition(n => { const c = { ...n }; delete c[device_id]; return c; });
    } catch (e) {
      alert('Erreur : ' + (e.response?.data?.error || e.message));
    } finally {
      setSavingNom(s => ({ ...s, [device_id]: false }));
    }
  }

  if (config === null) return <div className="skeleton skel-settings-block" />;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)', maxWidth: 720 }}>

      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-3)', padding: '11px 16px', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', fontSize: 13 }}>
        <Icon name="temperature" size={16} color="var(--accent)" style={{ marginTop: 1 }} />
        <div style={{ flex: 1 }}>
          <strong>Sondes MEATER</strong> — connecte l'app au cloud MEATER pour suivre les températures de cuisson en temps réel.
          Le <strong>mot de passe MEATER</strong> doit être configuré dans <code>backend/.env</code> (variable <code>MEATER_PASSWORD</code>) cote serveur — c'est un secret.
        </div>
      </div>

      {/* Card Compte */}
      <div className="card">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--space-4)' }}>
          <h3 className="settings-card-titre" style={{ margin: 0 }}>Compte MEATER</h3>
          <div className="settings-status">
            <span className={`settings-status-dot settings-status-dot--${config.token_obtenu_le ? 'on' : 'off'}`} />
            <span style={{ color: config.token_obtenu_le ? 'var(--success)' : 'var(--muted)', fontSize: 12 }}>
              {config.token_obtenu_le ? 'Token actif' : 'Pas encore connecte'}
            </span>
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div>
            <label className="form-label">Courriel du compte MEATER</label>
            <input
              className="form-input"
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="ton@email.com"
            />
          </div>

          <div className="muted" style={{ fontSize: 11 }}>
            {config.token_obtenu_le
              ? <>Dernière connexion cloud : <strong>{fmtDatetime(config.token_obtenu_le)}</strong></>
              : <>Après sauvegarde, clique <strong>Rafraîchir les sondes</strong> ci-dessous pour déclencher la connexion.</>}
          </div>

          <div style={{ display: 'flex', gap: 'var(--space-3)', marginTop: 4, flexWrap: 'wrap' }}>
            <button className="btn btn-primary btn--sm" onClick={enregistrerEmail} disabled={savingEmail}>
              <Icon name="sauvegarder" size={14} /> {savingEmail ? 'Enregistrement…' : 'Enregistrer le courriel'}
            </button>
          </div>

          {msg && (
            <div className={msg.type === 'success' ? 'settings-alerte settings-succes' : 'settings-alerte settings-erreur'} style={{ marginTop: 4 }}>
              <Icon name={msg.type === 'success' ? 'succes' : 'erreur'} size={14} /> {msg.text}
            </div>
          )}
        </div>
      </div>

      {/* Card Sondes détectées */}
      <div className="card">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--space-4)', flexWrap: 'wrap', gap: 'var(--space-3)' }}>
          <h3 className="settings-card-titre" style={{ margin: 0 }}>Sondes détectées</h3>
          <button className="btn btn-ghost btn--sm" onClick={rafraichirDevices} disabled={chargementDevices || !config.email}>
            <Icon name="rafraichir" size={13} /> {chargementDevices ? 'Chargement…' : 'Rafraichir les sondes'}
          </button>
        </div>

        {!config.email && (
          <div className="muted" style={{ fontSize: 13 }}>
            Enregistre d'abord le courriel MEATER ci-dessus.
          </div>
        )}

        {erreurDevices && (
          <div className="error-inline" style={{ marginBottom: 'var(--space-3)', flexDirection: 'column', alignItems: 'flex-start' }}>
            <span><Icon name="erreur" size={14} /> Impossible de joindre MEATER : {erreurDevices}</span>
            <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
              Vérifie que <code>MEATER_PASSWORD</code> est défini dans <code>backend/.env</code> et que le courriel est correct.
            </div>
          </div>
        )}

        {config.email && !erreurDevices && devices.length === 0 && !chargementDevices && (
          <div className="muted" style={{ fontSize: 13 }}>
            Aucune sonde détectée pour l'instant. Allume tes sondes MEATER et clique <strong>Rafraîchir les sondes</strong>.
          </div>
        )}

        {devices.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {devices.map(d => {
              const nomEnEdition = nomsEdition[d.id];
              const enEdition    = nomEnEdition !== undefined;
              const nomActuel    = d.nom || `Probe ${String(d.id).slice(-4)}`;
              const temp         = d.temperature?.internal;

              return (
                <div key={d.id} style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, padding: 12, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                  <div style={{ flex: '1 1 200px', minWidth: 0 }}>
                    {enEdition ? (
                      <input
                        className="form-input"
                        value={nomEnEdition}
                        onChange={e => setNomsEdition(n => ({ ...n, [d.id]: e.target.value }))}
                        placeholder="Ex: Porc — probe 1"
                        autoFocus
                        onKeyDown={e => { if (e.key === 'Enter') renommerDevice(d.id); }}
                      />
                    ) : (
                      <div style={{ fontWeight: 600, fontSize: 14 }}>{nomActuel}</div>
                    )}
                    <div className="muted" style={{ fontSize: 11, marginTop: 2, wordBreak: 'break-all' }}>
                      ID : {d.id}
                    </div>
                  </div>

                  <div style={{ textAlign: 'right', minWidth: 80 }}>
                    <div className="muted" style={{ fontSize: 11 }}>Dernière temp</div>
                    <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--gold-500)' }}>
                      {typeof temp === 'number' ? `${temp.toFixed(1)}\u00b0C` : '—'}
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: 6 }}>
                    {enEdition ? (
                      <>
                        <button
                          className="btn btn-primary"
                          style={{ fontSize: 12, padding: '6px 12px' }}
                          onClick={() => renommerDevice(d.id)}
                          disabled={savingNom[d.id] || !(nomEnEdition || '').trim()}
                        >
                          {savingNom[d.id] ? '…' : 'Enregistrer'}
                        </button>
                        <button
                          className="btn btn-ghost"
                          style={{ fontSize: 12, padding: '6px 12px' }}
                          onClick={() => setNomsEdition(n => { const c = { ...n }; delete c[d.id]; return c; })}
                        >
                          Annuler
                        </button>
                      </>
                    ) : (
                      <button
                        className="btn btn-ghost"
                        style={{ fontSize: 12, padding: '6px 12px' }}
                        onClick={() => setNomsEdition(n => ({ ...n, [d.id]: nomActuel }))}
                      >
                        Renommer
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Card seuils de cuisson */}
      <SectionSeuilsCuisson />
    </div>
  );
}

// ─── Seuils de cuisson par produit ───────────────────────────────────────────

const PRODUITS_SEUILS = [
  { key: 'porc',         label: 'Porc Effiloché' },
  { key: 'brisket',      label: 'Brisket' },
  { key: 'saucisse',     label: 'Saucisses' },
  { key: 'saumon',       label: 'Saumon Fumé' },
  { key: 'ribs',         label: 'Cotes Levees' },
];

function SectionSeuilsCuisson() {
  const [seuils, setSeuils]   = useState(null);
  const [edited, setEdited]   = useState({});
  const [saving, setSaving]   = useState(false);
  const [msg, setMsg]         = useState(null);

  useEffect(() => {
    api.get('/meater/config/seuils')
      .then(d => { setSeuils(d || {}); setEdited(d || {}); })
      .catch(() => { setSeuils({}); setEdited({}); });
  }, []);

  function champ(key, val) {
    setEdited(e => ({ ...e, [key]: val }));
  }

  async function enregistrer() {
    setSaving(true); setMsg(null);
    try {
      await api.put('/meater/config/seuils', edited);
      setSeuils({ ...edited });
      setMsg({ type: 'success', text: 'Seuils enregistres.' });
    } catch (e) {
      setMsg({ type: 'danger', text: e.message || 'Erreur' });
    } finally { setSaving(false); }
  }

  if (seuils === null) return null;

  return (
    <div className="card">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--space-4)', flexWrap: 'wrap', gap: 'var(--space-3)' }}>
        <h3 className="settings-card-titre" style={{ margin: 0 }}>Seuils de cuisson par produit (degres F)</h3>
        <button className="btn btn-primary btn--sm" onClick={enregistrer} disabled={saving}>
          <Icon name="sauvegarder" size={14} /> {saving ? 'Enregistrement…' : 'Enregistrer les seuils'}
        </button>
      </div>
      {msg && (
        <div className={msg.type === 'success' ? 'settings-alerte settings-succes' : 'settings-alerte settings-erreur'} style={{ marginBottom: 'var(--space-3)' }}>
          <Icon name={msg.type === 'success' ? 'succes' : 'erreur'} size={14} /> {msg.text}
        </div>
      )}
      <table className="esc-table">
        <thead>
          <tr>
            <th>Produit</th>
            <th>Salubrite (°F)</th>
            <th>Tendrete (°F)</th>
          </tr>
        </thead>
        <tbody>
          {PRODUITS_SEUILS.map(p => (
            <tr key={p.key}>
              <td style={{ fontWeight: 600 }}>{p.label}</td>
              <td>
                <input
                  type="number"
                  step="1"
                  value={edited[`temp_seuil_salubrite_${p.key}_f`] ?? ''}
                  onChange={e => champ(`temp_seuil_salubrite_${p.key}_f`, e.target.value)}
                  style={{ width: 90, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 5, color: 'var(--text)', padding: '4px 8px', fontSize: 13 }}
                  placeholder="ex: 160"
                />
              </td>
              <td>
                <input
                  type="number"
                  step="1"
                  value={edited[`temp_seuil_tendrete_${p.key}_f`] ?? ''}
                  onChange={e => champ(`temp_seuil_tendrete_${p.key}_f`, e.target.value)}
                  style={{ width: 90, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 5, color: 'var(--text)', padding: '4px 8px', fontSize: 13 }}
                  placeholder="ex: 190"
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="muted" style={{ fontSize: 11, marginTop: 10 }}>
        Salubrite = alerte rouge si non atteint (CCP). Tendrete = objectif qualite (warning).
        Valeurs en degres Fahrenheit. Defauts : Porc 160°F / 190°F, Brisket 160°F / 203°F, Saucisses 160°F / 160°F.
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════
// ONGLET — Production
// ═══════════════════════════════════════════════
const LABELS_PRODUITS = {
  porc: 'Porc Effiloché', ribs: 'Côtes Levées', brisket: 'Brisket',
  burnt_ends: 'Burnt Ends', saucisses: 'Saucisses', saumon: 'Saumon Fumé', sauce: 'Sauce BBQ',
};

function OngletProduction() {
  const [rows, setRows]             = useState([]);
  const [edited, setEdited]         = useState({});
  const [saving, setSaving]         = useState(false);
  const [msg, setMsg]               = useState(null);
  const [taux, setTaux]             = useState(null);
  const [loadingTaux, setLoadingTaux] = useState(false);

  useEffect(() => {
    api.get('/parametres/production').then(data => {
      setRows(data);
      const init = {};
      data.forEach(r => { init[r.produit] = { ...r }; });
      setEdited(init);
    }).catch(() => {});
  }, []);

  function champ(produit, key, val) {
    setEdited(e => ({ ...e, [produit]: { ...e[produit], [key]: val } }));
  }

  async function enregistrer() {
    setSaving(true); setMsg(null);
    try {
      for (const produit of Object.keys(edited)) {
        await api.put(`/parametres/production/${produit}`, edited[produit]);
      }
      setMsg({ type: 'success', text: 'Paramètres de production enregistrés.' });
    } catch (e) {
      setMsg({ type: 'danger', text: e.message || 'Erreur' });
    } finally { setSaving(false); }
  }

  async function rafraichirTaux() {
    setLoadingTaux(true);
    try {
      const res = await api.get('/parametres/production/taux-change');
      if (res.disponible) {
        setTaux({ taux: res.taux, date: res.date });
        setEdited(e => {
          const next = { ...e };
          Object.keys(next).forEach(p => { next[p] = { ...next[p], taux_change_usd: res.taux }; });
          return next;
        });
      } else {
        alert(res.message || 'Impossible de récupérer le taux');
      }
    } catch { alert('Erreur réseau'); } finally { setLoadingTaux(false); }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
      <div className="card" style={{ overflowX: 'auto' }}>
        <h3 className="settings-card-titre">Paramètres par produit</h3>
        <table className="esc-table">
          <thead>
            <tr>
              <th>Produit</th><th>Prix vente ($)</th><th>Rendement cible</th><th>Poids cible (g)</th><th>Taux USD→CAD</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => {
              const e = edited[r.produit] || r;
              return (
                <tr key={r.produit}>
                  <td style={{ fontWeight: 600 }}>{LABELS_PRODUITS[r.produit] || r.produit}</td>
                  <td><input type="number" className="form-input" style={{ width: 90 }} step="0.50" min="0" value={e.prix_vente_defaut ?? ''} placeholder="—" onChange={ev => champ(r.produit, 'prix_vente_defaut', ev.target.value === '' ? null : parseFloat(ev.target.value))} /></td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <input type="number" className="form-input" style={{ width: 70 }} step="1" min="0" max="100" value={e.rendement_cible_pct !== null && e.rendement_cible_pct !== undefined ? Math.round(e.rendement_cible_pct * 100) : ''} placeholder="—" onChange={ev => champ(r.produit, 'rendement_cible_pct', ev.target.value === '' ? null : parseFloat(ev.target.value) / 100)} />
                      <span className="muted">%</span>
                    </div>
                  </td>
                  <td><input type="number" className="form-input" style={{ width: 80 }} step="10" min="0" value={e.poids_unite_cible_g ?? ''} placeholder="—" onChange={ev => champ(r.produit, 'poids_unite_cible_g', ev.target.value === '' ? null : parseFloat(ev.target.value))} /></td>
                  <td><input type="number" className="form-input" style={{ width: 80 }} step="0.01" min="1" value={e.taux_change_usd ?? 1.38} onChange={ev => champ(r.produit, 'taux_change_usd', parseFloat(ev.target.value))} /></td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div className="muted" style={{ fontSize: 12, marginTop: 8 }}>Le rendement cible est utilisé pour estimer le nombre d'unités produites avant la pesée réelle.</div>
      </div>
      <div className="card" style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)', flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontWeight: 600, marginBottom: 2, fontSize: 14 }}>Taux de change USD vers CAD</div>
          {taux
            ? <div className="muted" style={{ fontSize: 12 }}>1 USD = {taux.taux} CAD ({taux.date})</div>
            : <div className="muted" style={{ fontSize: 12 }}>Source : Banque du Canada</div>}
        </div>
        <button className="btn btn-ghost btn--sm" onClick={rafraichirTaux} disabled={loadingTaux}>
          <Icon name="rafraichir" size={13} /> {loadingTaux ? 'Chargement…' : 'Rafraichir depuis Banque du Canada'}
        </button>
      </div>
      {msg && (
        <div className={msg.type === 'success' ? 'settings-alerte settings-succes' : 'settings-alerte settings-erreur'}>
          <Icon name={msg.type === 'success' ? 'succes' : 'erreur'} size={14} /> {msg.text}
        </div>
      )}
      <button className="btn btn-primary btn--sm" onClick={enregistrer} disabled={saving} style={{ alignSelf: 'flex-start' }}>
        <Icon name="sauvegarder" size={14} /> {saving ? 'Enregistrement…' : 'Enregistrer tous les changements'}
      </button>
    </div>
  );
}

// ═══════════════════════════════════════════════
// ONGLET — Base de données
// ═══════════════════════════════════════════════
function OngletBaseDeDonnees() {
  const [info, setInfo]             = useState(null);
  const [backing, setBacking]       = useState(false);
  const [restoreFile, setRestoreFile]   = useState(null);
  const [restoring, setRestoring]       = useState(false);
  const [confirmation, setConfirmation] = useState('');
  const fileRef                     = useRef();

  useEffect(() => {
    api.get('/parametres/db/info').then(setInfo).catch(() => {});
  }, []);

  async function telechargerBackup() {
    setBacking(true);
    try {
      const token = localStorage.getItem('escamel_token');
      const res = await fetch('/api/parametres/db/backup', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) { alert('Erreur lors de la sauvegarde'); return; }
      const blob = await res.blob();
      const date = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `escamel_backup_${date}.db`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { alert('Erreur: ' + e.message); } finally { setBacking(false); }
  }

  async function restaurer() {
    if (confirmation !== 'CONFIRMER RESTAURATION') {
      alert('Veuillez taper exactement "CONFIRMER RESTAURATION" pour valider.');
      return;
    }
    if (!restoreFile) { alert('Aucun fichier sélectionné.'); return; }
    setRestoring(true);
    try {
      const fd = new FormData();
      fd.append('db', restoreFile);
      const token = localStorage.getItem('escamel_token');
      const res = await fetch('/api/parametres/db/restore', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const json = await res.json();
      if (json.success) {
        alert(json.message);
      } else {
        alert('Erreur : ' + (json.error || 'inconnue'));
      }
    } catch (e) { alert('Erreur: ' + e.message); } finally { setRestoring(false); }
  }

  return (
    <div style={{ maxWidth: 560, display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
      <div className="card">
        <h3 className="settings-card-titre"><Icon name="document" size={14} style={{ marginRight: 6 }} /> Base de données — Escamel</h3>
        {info ? (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-2) var(--space-5)', fontSize: 14 }}>
            <div className="muted">Fichier</div><div style={{ fontFamily: 'monospace', fontSize: 12 }}>{info.fichier}</div>
            <div className="muted">Taille</div>
            <div>
              <span style={{ fontFamily: 'Cinzel', fontWeight: 600, fontFeatureSettings: '"tnum" 1' }}>{fmtMo(info.taille_octets)}</span>
            </div>
            <div className="muted">Transactions journal</div><div>{info.nb_journal}</div>
            <div className="muted">Actifs enregistrés</div><div>{info.nb_actifs}</div>
            <div className="muted">Scans agent</div><div>{info.nb_agent_usage}</div>
          </div>
        ) : (
          <div className="skeleton" style={{ height: 80, borderRadius: 'var(--radius-sm)' }} />
        )}
      </div>

      <div className="card">
        <h3 className="settings-card-titre"><Icon name="telecharger" size={14} style={{ marginRight: 6 }} /> Sauvegarde</h3>
        <div className="muted" style={{ fontSize: 13, marginBottom: 'var(--space-4)' }}>Télécharge une copie complète de la base de données.</div>
        <button className="btn btn-primary btn--sm" onClick={telechargerBackup} disabled={backing}>
          <Icon name="telecharger" size={14} /> {backing ? 'Préparation…' : 'Télécharger la sauvegarde'}
        </button>
        {!backing && <div className="muted" style={{ fontSize: 11, marginTop: 6 }}>escamel_backup_{new Date().toISOString().slice(0, 10).replace(/-/g, '')}.db</div>}
      </div>

      <div className="card" style={{ border: '1px solid var(--danger)' }}>
        <h3 className="settings-card-titre" style={{ color: 'var(--danger)' }}><Icon name="alerte" size={14} style={{ marginRight: 6 }} /> Restauration</h3>
        <div className="error-inline" style={{ marginBottom: 'var(--space-4)' }}>
          <Icon name="attention" size={14} />
          Cette opération remplace <strong>toutes les données actuelles</strong> par celles du fichier sélectionné.
        </div>
        <input ref={fileRef} type="file" accept=".db" style={{ display: 'none' }} onChange={e => setRestoreFile(e.target.files?.[0] || null)} />
        <button className="btn btn-ghost btn--sm" onClick={() => fileRef.current?.click()}>
          <Icon name="document" size={14} /> {restoreFile ? restoreFile.name : 'Choisir un fichier .db'}
        </button>
        <div style={{ marginTop: 'var(--space-4)' }}>
          <div className="field">
            <label>Confirmer la restauration — tapez exactement :</label>
            <code style={{ display: 'block', background: 'var(--surface)', border: '1px solid var(--border)', padding: '4px 8px', borderRadius: 'var(--radius-sm)', fontSize: 12, marginBottom: 8, fontFamily: 'monospace' }}>CONFIRMER RESTAURATION</code>
            <input
              placeholder="CONFIRMER RESTAURATION"
              value={confirmation}
              onChange={e => setConfirmation(e.target.value)}
              style={{ borderColor: confirmation === 'CONFIRMER RESTAURATION' ? 'var(--success)' : undefined }}
            />
          </div>
        </div>
        <button
          className="btn btn-danger btn--sm"
          style={{ marginTop: 'var(--space-3)' }}
          onClick={restaurer}
          disabled={restoring || confirmation !== 'CONFIRMER RESTAURATION' || !restoreFile}
        >
          {restoring ? 'Restauration…' : 'Restaurer la sauvegarde'}
        </button>
      </div>
    </div>
  );
}

function FaviconPreview({ ts }) {
  const [broken, setBroken] = React.useState(false);
  React.useEffect(() => setBroken(false), [ts]);
  if (broken) return <span style={{ fontSize: 20, color: 'var(--success)' }}>✓</span>;
  return (
    <img
      src={`/api/parametres/general/favicon?t=${ts}`}
      alt="Favicon"
      style={{ width: '100%', height: '100%', objectFit: 'contain' }}
      onError={() => setBroken(true)}
    />
  );
}

export default function Settings() {
  const [onglet, setOnglet]         = useState(() => {
    const p = new URLSearchParams(window.location.search);
    return p.get('tab') || 'general';
  });
  const [settings, setSettings]     = useState({});
  const [chargement, setCharge]     = useState(true);
  const [sauvegarde, setSauve]      = useState(false);
  const [succesMsg, setSucces]      = useState('');
  const [erreurMsg, setErreur]      = useState('');
  const [uploadLogo, setUpload]     = useState(false);
  const fileInputRef                = useRef(null);

  // Témoignages
  const [temoignages, setTemoignages] = useState([]);
  const [temoForm, setTemoForm]       = useState({ nom: '', texte: '', note: 5, source: '' });
  const [listeAttente, setListeAttente] = useState([]);

  // Backup
  const [modulesExport, setModulesExport] = useState({
    journal: true, batches: true, evenements: true,
    inventaire: true, clients_corpo: true, catalogue_produits: true,
  });
  const [tableImport, setTableImport]     = useState('journal');
  const [fichierImport, setFichierImport] = useState(null);
  const [historique, setHistorique]       = useState([]);
  const [loadingBackup, setLoadingBackup] = useState({});
  const [msgBackup, setMsgBackup]         = useState({ type: '', texte: '' });
  const fileImportRef                     = useRef(null);

  useEffect(() => {
    charger();
    chargerHistorique();
  }, []); // eslint-disable-line

  async function charger() {
    setCharge(true);
    try {
      const [rows, temos, liste] = await Promise.all([
        api.get('/settings'),
        api.get('/settings/temoignages').catch(() => []),
        api.get('/settings/liste-attente').catch(() => []),
      ]);
      const map = {};
      rows.forEach(s => { map[s.cle] = s.valeur; });
      setSettings(map);
      setTemoignages(Array.isArray(temos) ? temos : []);
      setListeAttente(Array.isArray(liste) ? liste : []);
    } catch {
      setErreur('Erreur de chargement des paramètres.');
    } finally {
      setCharge(false);
    }
  }

  function handleChange(cle, valeur) {
    setSettings(prev => ({ ...prev, [cle]: valeur }));
    setSucces('');
    setErreur('');
  }

  async function sauvegarder() {
    const cles = CLES_PAR_ONGLET[onglet];
    if (!cles) return;
    setSauve(true);
    setSucces('');
    setErreur('');
    try {
      for (const cle of cles) {
        if (settings[cle] !== undefined) {
          await api.put(`/settings/${cle}`, { valeur: settings[cle] });
        }
      }
      setSucces('Paramètres sauvegardés.');
    } catch {
      setErreur('Erreur lors de la sauvegarde.');
    } finally {
      setSauve(false);
    }
  }

  // ── Logo ──────────────────────────────────────
  async function uploaderLogo(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUpload(true);
    setSucces('');
    setErreur('');
    try {
      const formData = new FormData();
      formData.append('logo', file);
      const data = await api.postForm('/settings/logo', formData);
      setSettings(prev => ({ ...prev, logo_url: data.logo_url }));
      setSucces('Logo mis à jour.');
    } catch (err) {
      setErreur(err.response?.data?.error || err.message || 'Erreur lors du téléchargement du logo.');
    } finally {
      setUpload(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }

  // ── Témoignages ───────────────────────────────
  async function ajouterTemoignage() {
    if (!temoForm.nom.trim() || !temoForm.texte.trim()) return;
    try {
      await api.post('/settings/temoignages', temoForm);
      setTemoForm({ nom: '', texte: '', note: 5, source: '' });
      const temos = await api.get('/settings/temoignages').catch(() => []);
      setTemoignages(Array.isArray(temos) ? temos : []);
      setSucces('Témoignage ajouté.');
    } catch { setErreur('Erreur lors de l\'ajout.'); }
  }

  async function supprimerTemoignage(id) {
    if (!window.confirm('Supprimer ce témoignage ?')) return;
    try {
      await api.delete(`/settings/temoignages/${id}`);
      setTemoignages(t => t.filter(x => x.id !== id));
    } catch { setErreur('Erreur lors de la suppression.'); }
  }

  async function toggleTemoignage(t) {
    try {
      await api.put(`/settings/temoignages/${t.id}`, { ...t, actif: t.actif ? 0 : 1 });
      setTemoignages(prev => prev.map(x => x.id === t.id ? { ...x, actif: x.actif ? 0 : 1 } : x));
    } catch { setErreur('Erreur.'); }
  }

  // ── Backup ────────────────────────────────────
  async function chargerHistorique() {
    try {
      const data = await api.get('/backup/historique');
      setHistorique(Array.isArray(data) ? data : []);
    } catch (_) {}
  }

  async function backupDB() {
    setLoadingBackup(l => ({ ...l, db: true }));
    setMsgBackup({ type: '', texte: '' });
    try {
      const res = await api.post('/backup/manuel', { type: 'db_seul', cree_par: 'LCU' });
      setMsgBackup({ type: 'succes', texte: `Backup créé : ${res.fichier} (${res.taille_mb} MB)` });
      chargerHistorique();
    } catch (err) {
      setMsgBackup({ type: 'erreur', texte: err.response?.data?.error || 'Erreur lors du backup' });
    } finally {
      setLoadingBackup(l => ({ ...l, db: false }));
    }
  }

  async function telechargerBlob(url, nomFichier) {
    setMsgBackup({ type: '', texte: '' });
    try {
      const blob = await api.blob(url);
      const href = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = href;
      a.download = nomFichier;
      a.click();
      URL.revokeObjectURL(href);
    } catch {
      setMsgBackup({ type: 'erreur', texte: 'Erreur lors du téléchargement.' });
    }
  }

  function telechargerComplet() {
    const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
    telechargerBlob('/backup/complet', `escamel_backup_${ts}.zip`);
  }

  function telechargerCSV(table) {
    const ts = new Date().toISOString().slice(0, 10);
    telechargerBlob(`/backup/csv/${table}`, `escamel_${table}_${ts}.csv`);
  }

  async function exporterExcel() {
    const modules = Object.entries(modulesExport).filter(([, v]) => v).map(([k]) => k);
    if (modules.length === 0) {
      setMsgBackup({ type: 'erreur', texte: 'Sélectionnez au moins un module.' });
      return;
    }
    setLoadingBackup(l => ({ ...l, excel: true }));
    setMsgBackup({ type: '', texte: '' });
    try {
      const token = localStorage.getItem('escamel_token');
      const res   = await fetch('/api/backup/export-excel', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body:    JSON.stringify({ modules }),
      });
      if (!res.ok) throw new Error('Export échoué');
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = `escamel_export_${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setMsgBackup({ type: 'erreur', texte: 'Erreur lors de l\'export Excel.' });
    } finally {
      setLoadingBackup(l => ({ ...l, excel: false }));
    }
  }

  async function importerCSV() {
    if (!fichierImport) return;
    if (!window.confirm(`Importer "${fichierImport.name}" dans "${tableImport}" ?\nLes doublons seront ignorés automatiquement.`)) return;
    setLoadingBackup(l => ({ ...l, import: true }));
    setMsgBackup({ type: '', texte: '' });
    try {
      const formData = new FormData();
      formData.append('fichier', fichierImport);
      const res = await api.post(`/backup/import-csv/${tableImport}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setMsgBackup({
        type: 'succes',
        texte: `${res.importes}/${res.total} lignes importées${res.erreurs?.length ? ` — ${res.erreurs.length} erreur(s)` : ''}.`,
      });
      setFichierImport(null);
      if (fileImportRef.current) fileImportRef.current.value = '';
    } catch (err) {
      setMsgBackup({ type: 'erreur', texte: err.response?.data?.error || 'Erreur lors de l\'import' });
    } finally {
      setLoadingBackup(l => ({ ...l, import: false }));
    }
  }

  if (chargement) return (
    <div className="settings-page">
      <div className="skeleton skel-settings-title" />
      <div className="skeleton skel-settings-sub" style={{ marginBottom: 'var(--space-5)' }} />
      <div className="skeleton skel-settings-tabs" />
      <div className="skeleton skel-settings-block" />
    </div>
  );

  const logoPreview = settings.logo_url || null;

  return (
    <div className="settings-page">
      <div className="settings-header">
        <h1 className="settings-titre">Parametres</h1>
        <p className="settings-sous-titre">Configuration générale de l'application Escamel</p>
      </div>

      {/* ── ONGLETS ── */}
      <div className="settings-tabs">
        {ONGLETS.map(o => (
          <button
            key={o.id}
            className={`settings-tab${onglet === o.id ? ' actif' : ''}`}
            onClick={() => { setOnglet(o.id); setSucces(''); setErreur(''); setMsgBackup({ type: '', texte: '' }); }}
          >
            {o.label}
          </button>
        ))}
      </div>

      {/* Alertes globales */}
      {succesMsg && (
        <div className="settings-alerte settings-succes">
          <Icon name="succes" size={14} /> {succesMsg}
        </div>
      )}
      {erreurMsg && (
        <div className="settings-alerte settings-erreur">
          <Icon name="erreur" size={14} /> {erreurMsg}
        </div>
      )}

      {/* ═══════════════════ ONGLET GÉNÉRAL ═══════════════════ */}
      {onglet === 'general' && (
        <section className="settings-section">
          <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)', marginBottom: 'var(--space-4)' }}>
            <h3 className="settings-card-titre">Informations générales</h3>
            {[
              { label: 'Nom entreprise', key: 'nom_entreprise', type: 'text',  placeholder: 'Escamel Artisans du Fumoir' },
              { label: 'Email contact',  key: 'email_contact',  type: 'email', placeholder: 'commandes@escamel.ca' },
              { label: 'Téléphone',      key: 'tel_contact',    type: 'tel',   placeholder: '418-555-0000' },
              { label: 'URL site web',   key: 'site_url',       type: 'url',   placeholder: 'https://escamel.ca' },
            ].map(({ label, key, type, placeholder }) => (
              <div className="field" key={key}>
                <label>{label}</label>
                <input type={type} value={settings[key] || ''} onChange={e => handleChange(key, e.target.value)} placeholder={placeholder} />
              </div>
            ))}
            <div className="field">
              <label>Countdown sur la page événement</label>
              <div className="settings-radio-groupe">
                <label className="settings-radio">
                  <input type="radio" name="countdown_actif" value="1" checked={settings.countdown_actif === '1'} onChange={() => handleChange('countdown_actif', '1')} />
                  <span>Oui</span>
                </label>
                <label className="settings-radio">
                  <input type="radio" name="countdown_actif" value="0" checked={settings.countdown_actif === '0'} onChange={() => handleChange('countdown_actif', '0')} />
                  <span>Non</span>
                </label>
              </div>
            </div>
            <div className="field">
              <label>Message d'accueil par défaut événements</label>
              <textarea className="settings-input" rows={2} style={{ resize: 'vertical' }} value={settings.message_accueil_defaut || ''} onChange={e => handleChange('message_accueil_defaut', e.target.value)} placeholder="Merci de votre inscription…" />
            </div>
          </div>

          <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
            <h3 className="settings-card-titre">Page Hub (events.escamel.ca)</h3>
            {[
              { label: 'Titre',       key: 'hub_titre',       placeholder: 'BBQ Escamel', rows: 0 },
              { label: 'Sous-titre',  key: 'hub_sous_titre',  placeholder: "L'art du BBQ texan authentique", rows: 0 },
            ].map(({ label, key, placeholder }) => (
              <div className="field" key={key}>
                <label>{label}</label>
                <input type="text" value={settings[key] || ''} onChange={e => handleChange(key, e.target.value)} placeholder={placeholder} />
              </div>
            ))}
            <div className="field">
              <label>Description</label>
              <textarea className="settings-input" rows={3} style={{ resize: 'vertical' }} value={settings.hub_description || ''} onChange={e => handleChange('hub_description', e.target.value)} placeholder="Description courte…" />
            </div>
            <div className="field">
              <label>Dates à venir annoncées (JSON)</label>
              <textarea className="settings-input" rows={2} style={{ resize: 'vertical', fontFamily: 'monospace', fontSize: 13 }} value={settings.dates_annoncees || '[]'} onChange={e => handleChange('dates_annoncees', e.target.value)} placeholder='["Automne 2026", "Hiver 2026-2027"]' />
              <span className="field-hint">Format JSON : ["Automne 2026"]</span>
            </div>
          </div>

          <div className="settings-actions">
            <button className="btn btn-primary btn--sm" onClick={sauvegarder} disabled={sauvegarde}>
              <Icon name="sauvegarder" size={14} /> {sauvegarde ? 'Sauvegarde…' : 'Sauvegarder'}
            </button>
          </div>
        </section>
      )}

      {/* ═══════════════════ ONGLET VISUEL ═══════════════════ */}
      {onglet === 'visuel' && (
        <section className="settings-section">
          <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
            <h3 className="settings-card-titre">Identite visuelle</h3>

            <div className="field">
              <label>Logo</label>
              <div className="settings-upload-zone">
                <div className="settings-upload-preview">
                  {logoPreview
                    ? <img src={logoPreview} alt="Logo" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                    : <Icon name="entreprise" size={28} color="var(--muted)" />}
                </div>
                <div className="settings-upload-info">
                  <input ref={fileInputRef} type="file" accept=".png,.jpg,.jpeg,.svg,.webp" onChange={uploaderLogo} style={{ display: 'none' }} />
                  <button className="btn btn-ghost btn--sm" onClick={() => fileInputRef.current?.click()} disabled={uploadLogo}>
                    <Icon name="televerser" size={14} /> {uploadLogo ? 'Envoi…' : 'Changer le logo'}
                  </button>
                  <span className="muted" style={{ fontSize: 11 }}>PNG, SVG ou JPG — max 2 Mo</span>
                </div>
              </div>
            </div>

            <div className="field">
              <label>Couleur principale (Hub public)</label>
              <div className="settings-couleur-wrap">
                <input type="color" value={settings.couleur_primaire || '#c8941a'} onChange={e => handleChange('couleur_primaire', e.target.value)} className="settings-color-picker" />
                <input type="text" value={settings.couleur_primaire || '#c8941a'} onChange={e => handleChange('couleur_primaire', e.target.value)} className="settings-input settings-input-court" placeholder="#c8941a" />
              </div>
            </div>
          </div>

          <div className="settings-actions">
            <button className="btn btn-primary btn--sm" onClick={sauvegarder} disabled={sauvegarde}>
              <Icon name="sauvegarder" size={14} /> {sauvegarde ? 'Sauvegarde…' : 'Sauvegarder'}
            </button>
          </div>
        </section>
      )}

      {/* ═══════════════════ ONGLET URLS ═══════════════════ */}
      {onglet === 'urls' && (
        <section className="settings-section">
          <div className="settings-alerte settings-erreur" style={{ marginBottom: 'var(--space-5)', background: 'color-mix(in srgb, var(--warning) 8%, transparent)', border: '1px solid color-mix(in srgb, var(--warning) 30%, transparent)', color: 'var(--text-soft)' }}>
            <Icon name="attention" size={14} color="var(--warning)" />
            Modifier ces URLs seulement lors du déploiement. Un mauvais URL peut casser les liens clients.
          </div>

          <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
            <h3 className="settings-card-titre">URLs de l'application</h3>
            {[
              { label: 'Backend API',                               key: 'url_backend',          hint: 'Utilisé par : App interne + formulaires publics',            placeholder: 'http://localhost:3001' },
              { label: 'Frontend Evenements (events.escamel.ca)',   key: 'url_frontend_events',  hint: 'Utilisé par : Liens "Voir le formulaire public" + emails',    placeholder: 'http://localhost:3002' },
              { label: 'Frontend Corporatif',                       key: 'url_frontend_corpo',   hint: 'Utilisé par : Liens contrats corporatifs',                   placeholder: 'http://localhost:3002' },
              { label: 'App interne',                               key: 'url_frontend_app',     hint: 'Utilisé par : Liens de retour dans les emails',              placeholder: 'http://localhost:3000' },
            ].map(({ label, key, hint, placeholder }) => (
              <div className="field" key={key}>
                <label>{label}</label>
                <input type="url" value={settings[key] || ''} onChange={e => handleChange(key, e.target.value)} placeholder={placeholder} />
                <span className="field-hint">{hint}</span>
              </div>
            ))}
          </div>

          <div className="settings-actions">
            <button className="btn btn-primary btn--sm" onClick={sauvegarder} disabled={sauvegarde}>
              <Icon name="sauvegarder" size={14} /> {sauvegarde ? 'Sauvegarde…' : 'Sauvegarder'}
            </button>
          </div>
        </section>
      )}

      {/* ═══════════════════ ONGLET FISCAL ═══════════════════ */}
      {onglet === 'fiscal' && (
        <section className="settings-section">
          <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
            <h3 className="settings-card-titre"><Icon name="recu" size={14} style={{ marginRight: 6 }} /> Identification fiscale</h3>
            <div className="field">
              <label>Numero TPS Escamel</label>
              <input type="text" value={settings.numero_tps || ''} onChange={e => handleChange('numero_tps', e.target.value)} placeholder="RT-0001-XXXX" style={{ maxWidth: 200 }} />
              <span className="field-hint">Apparaît sur les factures et contrats corpo</span>
            </div>
            <div className="field">
              <label>Numero TVQ Escamel</label>
              <input type="text" value={settings.numero_tvq || ''} onChange={e => handleChange('numero_tvq', e.target.value)} placeholder="1234567890 TQ 0001" style={{ maxWidth: 240 }} />
            </div>
            <div className="field">
              <label>NEQ (Numero entreprise Quebec)</label>
              <input type="text" value={settings.neq || ''} onChange={e => handleChange('neq', e.target.value)} placeholder="3381552556" style={{ maxWidth: 160 }} />
            </div>
            <div className="field">
              <label>Adresse Escamel (pour factures corpo)</label>
              <input type="text" value={settings.adresse_escamel || ''} onChange={e => handleChange('adresse_escamel', e.target.value)} placeholder="120 ch. McCune, Stoneham-et-Tewkesbury QC G3C 0Z6" />
            </div>
            <div className="field">
              <label>Acompte par défaut</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                <input type="number" min="0" max="100" step="5" value={settings.acompte_defaut_pct || '40'} onChange={e => handleChange('acompte_defaut_pct', e.target.value)} style={{ maxWidth: 80, background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', color: 'var(--text)', padding: '9px 12px' }} />
                <span className="muted" style={{ fontSize: 13 }}>% du montant total du contrat</span>
              </div>
            </div>
          </div>

          <div className="settings-actions">
            <button className="btn btn-primary btn--sm" onClick={sauvegarder} disabled={sauvegarde}>
              <Icon name="sauvegarder" size={14} /> {sauvegarde ? 'Sauvegarde…' : 'Sauvegarder'}
            </button>
          </div>
        </section>
      )}

      {/* ═══════════════════ ONGLET SAUVEGARDE ═══════════════════ */}
      {onglet === 'sauvegarde' && (
        <section className="settings-section">
          {msgBackup.texte && (
            <div className={`settings-alerte ${msgBackup.type === 'succes' ? 'settings-succes' : 'settings-erreur'}`}>
              <Icon name={msgBackup.type === 'succes' ? 'succes' : 'erreur'} size={14} /> {msgBackup.texte}
            </div>
          )}

          {/* Backup rapide */}
          <div className="backup-bloc">
            <h4 className="backup-sous-titre">Backup rapide</h4>
            <div className="backup-actions">
              <button className="btn btn-ghost backup-btn" onClick={backupDB} disabled={loadingBackup.db}>
                <Icon name="sauvegarder" size={14} /> {loadingBackup.db ? 'Backup…' : 'Backup DB maintenant'}
              </button>
              <span className="muted" style={{ fontSize: 12 }}>Sauvegarde la DB dans database/backups/</span>
            </div>
            <div className="backup-actions">
              <button className="btn btn-ghost backup-btn backup-btn-complet" onClick={telechargerComplet}>
                <Icon name="telecharger" size={14} /> Télécharger package complet
              </button>
              <span className="muted" style={{ fontSize: 12 }}>DB + tous les fichiers (reçus, logos, photos) en ZIP</span>
            </div>
          </div>

          {/* Export sélectif Excel */}
          <div className="backup-bloc">
            <h4 className="backup-sous-titre">Export sélectif Excel</h4>
            <div className="backup-modules-grid">
              {Object.entries({
                journal: 'Journal comptable',
                batches: 'Batches production',
                evenements: 'Événements',
                inventaire: 'Inventaire matières',
                clients_corpo: 'Clients corporatifs',
                catalogue_produits: 'Catalogue produits',
              }).map(([key, label]) => (
                <label key={key} className="backup-module-check">
                  <input type="checkbox" checked={modulesExport[key]} onChange={e => setModulesExport(prev => ({ ...prev, [key]: e.target.checked }))} />
                  <span>{label}</span>
                </label>
              ))}
            </div>
            <div className="backup-actions" style={{ marginTop: 12 }}>
              <button className="btn btn-primary btn--sm" onClick={exporterExcel} disabled={loadingBackup.excel}>
                <Icon name="telecharger" size={14} /> {loadingBackup.excel ? 'Export…' : 'Exporter en Excel'}
              </button>
            </div>
          </div>

          {/* Export CSV */}
          <div className="backup-bloc">
            <h4 className="backup-sous-titre">Export CSV par table</h4>
            <div className="backup-csv-grid">
              {[
                { table: 'journal',             label: 'Journal' },
                { table: 'batches',             label: 'Batches' },
                { table: 'inventaire_matieres', label: 'Inv. matières' },
                { table: 'inventaire_produits', label: 'Inv. produits' },
                { table: 'evenements',          label: 'Événements' },
                { table: 'clients_corpo',       label: 'Clients corpo' },
                { table: 'catalogue_produits',  label: 'Catalogue' },
              ].map(({ table, label }) => (
                <button key={table} className="btn btn-ghost backup-csv-btn" onClick={() => telechargerCSV(table)}>
                  <Icon name="telecharger" size={13} /> {label}
                </button>
              ))}
            </div>
          </div>

          {/* Import CSV */}
          <div className="backup-bloc">
            <h4 className="backup-sous-titre">Import CSV</h4>
            <div className="backup-import-row">
              <select value={tableImport} onChange={e => setTableImport(e.target.value)} className="settings-input" style={{ width: 'auto', flex: '0 0 auto' }}>
                <option value="journal">Journal comptable</option>
                <option value="inventaire_matieres">Inventaire matières</option>
                <option value="catalogue_produits">Catalogue produits</option>
              </select>
              <input ref={fileImportRef} type="file" accept=".csv" onChange={e => setFichierImport(e.target.files[0] || null)} className="settings-input backup-input-fichier" />
              {fichierImport && (
                <button className="btn btn-primary btn--sm" onClick={importerCSV} disabled={loadingBackup.import}>
                  <Icon name="televerser" size={14} /> {loadingBackup.import ? 'Import…' : 'Importer'}
                </button>
              )}
            </div>
            <span className="settings-aide muted">Les doublons sont ignorés automatiquement. Format : CSV UTF-8 avec en-têtes.</span>
          </div>

          {/* Historique */}
          <div className="backup-bloc">
            <h4 className="backup-sous-titre">Historique des backups</h4>
            {historique.length === 0
              ? <p className="muted" style={{ fontSize: 13 }}>Aucun backup enregistré.</p>
              : (
                <div className="esc-table-wrap">
                  <table className="esc-table">
                    <thead>
                      <tr><th>Date</th><th>Type</th><th>Taille</th><th>Par</th><th>Notes</th></tr>
                    </thead>
                    <tbody>
                      {historique.map(b => (
                        <tr key={b.id}>
                          <td style={{ fontSize: 12 }}>{b.cree_le ? new Date(b.cree_le).toLocaleString('fr-CA') : '—'}</td>
                          <td><span className={`badge badge-backup-${b.type}`}>{b.type}</span></td>
                          <td className="muted">{b.taille_mb ? `${b.taille_mb} MB` : '—'}</td>
                          <td>{b.cree_par || '—'}</td>
                          <td className="muted" style={{ fontSize: 12 }}>{b.notes || ''}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )
            }
          </div>
        </section>
      )}

      {/* ═══════════════════ ONGLET TÉMOIGNAGES ═══════════════════ */}
      {onglet === 'temoignages' && (
        <section className="settings-section">
          <p className="settings-aide muted" style={{ marginBottom: 16 }}>
            Affichés sur la page hub events.escamel.ca
          </p>

          {/* Formulaire ajout */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
            <div className="settings-champ" style={{ margin: 0 }}>
              <label className="settings-label">Nom</label>
              <input type="text" className="settings-input" value={temoForm.nom} onChange={e => setTemoForm(f => ({ ...f, nom: e.target.value }))} placeholder="Jean T., Québec" />
            </div>
            <div className="settings-champ" style={{ margin: 0 }}>
              <label className="settings-label">Source</label>
              <input type="text" className="settings-input" value={temoForm.source} onChange={e => setTemoForm(f => ({ ...f, source: e.target.value }))} placeholder="Google, Facebook…" />
            </div>
          </div>

          <div className="settings-champ">
            <label className="settings-label">Témoignage</label>
            <textarea className="settings-input" rows={2} style={{ resize: 'vertical' }} value={temoForm.texte} onChange={e => setTemoForm(f => ({ ...f, texte: e.target.value }))} placeholder="Meilleur BBQ de ma vie !" />
          </div>

          <div className="field" style={{ flexDirection: 'row', alignItems: 'center', gap: 'var(--space-3)' }}>
            <label style={{ margin: 0, whiteSpace: 'nowrap' }}>Note</label>
            <select className="settings-input" style={{ width: 'auto', padding: '6px 12px' }} value={temoForm.note} onChange={e => setTemoForm(f => ({ ...f, note: +e.target.value }))}>
              {[5, 4, 3, 2, 1].map(n => <option key={n} value={n}>{n} / 5</option>)}
            </select>
            <button className="btn btn-primary btn--sm" onClick={ajouterTemoignage}>
              <Icon name="ajouter" size={14} /> Ajouter
            </button>
          </div>

          {temoignages.length === 0 && (
            <p className="muted" style={{ fontSize: 13, marginTop: 12 }}>Aucun témoignage. Ajoutez-en un ci-dessus.</p>
          )}

          <div style={{ marginTop: 'var(--space-4)', display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
            {temoignages.map(t => (
              <div key={t.id} className="card" style={{ padding: '12px 16px', opacity: t.actif ? 1 : 0.55 }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-3)' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ color: 'var(--accent)', fontSize: 13, letterSpacing: '0.05em' }}>{'★'.repeat(t.note || 5)}</div>
                    <div style={{ fontSize: 13, fontStyle: 'italic', margin: '4px 0', color: 'var(--text-soft)' }}>"{t.texte}"</div>
                    <div className="muted" style={{ fontSize: 12 }}>— {t.nom}{t.source ? ` · ${t.source}` : ''}</div>
                  </div>
                  <div style={{ display: 'flex', gap: 'var(--space-2)', flexShrink: 0 }}>
                    <button className="btn btn-ghost btn--sm" onClick={() => toggleTemoignage(t)}>
                      {t.actif ? 'Masquer' : 'Afficher'}
                    </button>
                    <button className="btn btn-danger btn--sm" onClick={() => supprimerTemoignage(t.id)}>
                      <Icon name="supprimer" size={13} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Liste d'attente */}
          {listeAttente.length > 0 && (
            <>
              <div className="settings-sep" />
              <h2 className="settings-section-titre">Liste d'attente événements</h2>
              <p className="settings-aide muted" style={{ marginBottom: 12 }}>
                {listeAttente.length} inscrit(s)
              </p>
              <div className="esc-table-wrap">
                <table className="esc-table">
                  <thead>
                    <tr><th>Courriel</th><th>Prénom</th><th>Source</th><th>Inscrit le</th></tr>
                  </thead>
                  <tbody>
                    {listeAttente.slice(0, 20).map(e => (
                      <tr key={e.id}>
                        <td>{e.email}</td>
                        <td>{e.prenom || '—'}</td>
                        <td>{e.source || 'hub'}</td>
                        <td className="muted" style={{ fontSize: 12 }}>
                          {e.cree_le ? new Date(e.cree_le).toLocaleDateString('fr-CA') : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {listeAttente.length > 20 && (
                  <p className="muted" style={{ fontSize: 12, marginTop: 8 }}>
                    + {listeAttente.length - 20} autres inscrits
                  </p>
                )}
              </div>
            </>
          )}
        </section>
      )}

      {/* ═══════════════════ ONGLET API CLAUDE ═══════════════════ */}
      {onglet === 'api_claude' && <OngletApiClaude />}

      {/* ═══════════════════ ONGLET ENTREPRISE ═══════════════════ */}
      {onglet === 'entreprise' && <OngletEntreprise />}

      {/* ═══════════════════ ONGLET ASSOCIÉS ═══════════════════ */}
      {onglet === 'associes' && <OngletAssocies />}

      {/* ═══════════════════ ONGLET ALERTES ═══════════════════ */}
      {onglet === 'alertes' && <OngletAlertes />}

      {/* ═══════════════════ ONGLET INTÉGRATIONS ═══════════════════ */}
      {onglet === 'integrations' && <OngletIntegrations onAllerSecrets={() => setOnglet('secrets')} />}

      {/* ═══════════════════ ONGLET MEATER ═══════════════════ */}
      {onglet === 'meater' && <OngletMeater />}

      {/* ═══════════════════ ONGLET PRODUCTION ═══════════════════ */}
      {onglet === 'production' && <OngletProduction />}

      {/* ═══════════════════ ONGLET BASE DE DONNÉES ═══════════════════ */}
      {onglet === 'db' && <OngletBaseDeDonnees />}

      {/* ═══════════════════ ONGLET SECRETS ═══════════════════ */}
      {onglet === 'secrets' && <SettingsSecrets />}
    </div>
  );
}
